// Sandy's action vocabulary: every <action .../> tag the model may emit, and
// what confirming one actually does. Mirrors TOUR_REGISTRY's role for tours.
//
// Safety model (Sandy 2026-08-10): tags NEVER execute on their own — each
// renders an inert confirm chip, and the click calls claude-teacher-api with
// the TEACHER'S OWN token (the same call path as the page's real buttons), so
// ownership and role enforcement are the server's, not the model's. Unknown
// kinds or failed validation degrade the tag to visible text, exactly like an
// unknown tour id. The kill switch REACT_APP_SANDY_ACTIONS=off turns every
// action tag into plain text.

import {
  teacherApi, releaseStudent, updateDueDates, sendReminder, fetchClasses,
  invalidateRoster, fetchRoster,
} from './api';
import { MAX_CHECKPOINTS } from './checkpointSchedule';
import { unitTitle } from './unitScope';

export function sandyActionsEnabled() {
  return process.env.REACT_APP_SANDY_ACTIONS !== 'off';
}

// assignPractice (a whole-domain set in one click) is gone (W4-B, review
// finding 3): it skipped the concept picker and review. A reply that still
// carries it shows its words with no chip; new work goes through
// draftAssignment, where the teacher presses Assign.

// Chips that only open something (never change class state): no confirm step.
export const VIEW_ACTION_KINDS = ['showQuestion', 'openStudent', 'draftAssignment'];
// Chips that open the dashboard's own dialog (2026-10-06): the dialog holds
// the inputs and the confirm, so the chip never runs anything. ChatActionChip
// renders these; execute() is never called. Count toward the cap of 2.
// removeStudent, manageClass, newClass (2026-10-13, BBO-82: "removing students
// and managing your classes should be completely doable through the AI").
// editHomework (2026-10-16): the assigned-set edit box, with Buffy's change filled in.
export const DIALOG_ACTION_KINDS = ['resetPassword', 'resetDiagnostic', 'pauseDiagnostic', 'setDiagnosticTime', 'finishHomework', 'editHomework', 'liveSession', 'removeStudent', 'manageClass', 'newClass'];

// student?, name? and test? (1-3) for the diagnostic dialog actions.
const diagnosticTarget = (a) => {
  const student = String(a.student || '').trim();
  const name = String(a.name || '').replace(/\s+/g, ' ').trim().slice(0, 80);
  const test = [1, 2, 3].includes(Number(a.test)) ? Number(a.test) : undefined;
  return { student: student || undefined, name: student ? name || undefined : undefined, test };
};
const whose = (p) => (p.student && p.name ? `${p.name}'s` : "a student's");
const clean = (v, max) => String(v || '').replace(/\s+/g, ' ').trim().slice(0, max);
const CLASS_SECTIONS = { details: 'Rename this class', students: 'Add or remove students', coteachers: 'Manage co-teachers', delete: 'Delete or leave this class' };

const futureIso = (v) => {
  const t = Date.parse(v);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
};
// A due date already gone (W4-B): the chip is a plain line, never a button.
export const PAST_DUE = 'That date has already passed, so I can’t move the due date to it. Tell me a later day.';
const isPast = (iso) => Number.isFinite(Date.parse(iso)) && Date.parse(iso) <= Date.now();
const whenLabel = (iso) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// Chip labels come from the model with no length rule. The old hard cut at 30
// characters split words ("Dahlia writing program questio"); keep up to 48 and
// shorten at a word boundary with an ellipsis.
export const LABEL_MAX = 48;
export const chipLabel = (v) => {
  const t = String(v || '').replace(/\s+/g, ' ').trim();
  if (t.length <= LABEL_MAX) return t || null;
  const cut = t.slice(0, LABEL_MAX - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space >= LABEL_MAX / 2 ? cut.slice(0, space) : cut).replace(/[\s,.;:–-]+$/, '')}…`;
};

// Each kind: validate(attrs) -> params | null; describe(params) -> chip text;
// execute(auth, classId, params) -> done text for the transcript.
export const SANDY_ACTIONS = {
  release: {
    validate: (a) => {
      const student = String(a.student || '').trim();
      const mode = a.mode === 'lesson_check' ? 'lesson_check' : 'check';
      const name = String(a.name || '').trim();
      return student && name ? { student, mode, name, domainId: a.domain || undefined } : null;
    },
    describe: (p) => (p.mode === 'lesson_check'
      ? `Release ${p.name} — relearn what they missed, then the check`
      : `Release ${p.name} straight to the check retake`),
    execute: async (auth, classId, p) => {
      // releaseStudent drops the class's caches and tells the pages (audit F9).
      await releaseStudent(auth, classId, p.student, p.mode, p.domainId);
      return `${p.name} released ${p.mode === 'lesson_check' ? 'to the lesson, then the check' : 'to the check'}.`;
    },
  },
  dueDate: {
    validate: (a) => {
      const assignment = Number(a.assignment);
      const due = futureIso(a.due);
      return Number.isInteger(assignment) && assignment >= 1 && assignment <= MAX_CHECKPOINTS && due ? { assignment, due } : null;
    },
    // unitName (prepare): the chip says which unit's checkpoint it moves.
    describe: (p) => `Move ${p.unitName ? `the ${p.unitName} unit’s` : 'the current unit’s'} checkpoint ${p.assignment} to ${whenLabel(p.due)}`,
    // Before the chip shows (W4-B): a past date is refused, and the unit is
    // named from the class's own data (the cached class list).
    prepare: async (auth, classId, p) => {
      if (isPast(p.due)) return { refused: PAST_DUE };
      try {
        const cls = ((await fetchClasses(auth)) || []).find((c) => c.id === classId || c.classID === classId);
        const dues = cls?.unit?.dueDates;
        if (!Array.isArray(dues) || p.assignment > dues.length) return { refused: `This class’s current unit has no checkpoint ${p.assignment}.` };
        return { params: { ...p, unitName: unitTitle(cls.unit) } };
      } catch { return {}; }
    },
    execute: async (auth, classId, p) => {
      if (isPast(p.due)) throw Object.assign(new Error(PAST_DUE), { refused: true });
      // updateDueDates writes every slot: merge the one change into the
      // unit's current dates, fetched fresh so a stale UI can't clobber edits.
      const classes = await fetchClasses(auth, { force: true });
      const cls = (classes || []).find((c) => c.id === classId || c.classID === classId);
      const unit = cls?.unit;
      const dues = Array.isArray(unit?.dueDates) ? [...unit.dueDates] : null;
      if (!dues || !dues.length || dues.length > MAX_CHECKPOINTS || (unit.checkpointMode !== 'continuous' && dues.length !== 4) || dues.some((due) => !futureIso(due)) || !futureIso(p.due)
        || !Number.isInteger(p.assignment) || p.assignment < 1 || p.assignment > dues.length) {
        throw new Error('This checkpoint is not available in the current unit. Refresh the unit plan.');
      }
      dues[p.assignment - 1] = p.due;
      await updateDueDates(auth, classId, unit.unitSeq, dues);
      return `Checkpoint ${p.assignment} is now due ${new Date(p.due).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`;
    },
  },
  markAbsent: {
    validate: (a) => {
      const student = String(a.student || '').trim();
      const name = String(a.name || '').trim();
      return student && name ? { student, name } : null;
    },
    describe: (p) => `Mark ${p.name} absent for this session`,
    execute: async (auth, classId, p) => {
      await teacherApi(auth, 'markAbsent', { classID: classId, userId: p.student });
      return `${p.name} marked absent.`;
    },
  },
  remind: {
    validate: (a) => {
      const student = String(a.student || '').trim();
      const name = String(a.name || '').trim();
      return student && name ? { student, name } : null;
    },
    describe: (p) => `Send ${p.name} a practice reminder`,
    execute: async (auth, classId, p) => {
      await sendReminder(auth, classId, p.student);
      invalidateRoster(classId);
      return `Reminder sent to ${p.name}.`;
    },
  },
  // View-only (2026-08-14, Sandy's night feedback): opens the actual question
  // in a popup so "the meteor-shower one" becomes something the teacher can
  // SEE. AIAssistant intercepts this kind and renders the modal itself —
  // execute() here is never called.
  showQuestion: {
    validate: (a) => {
      const record = String(a.record || a.recordId || '').trim();
      if (!record || !/^[\w.-]{1,64}$/.test(record)) return null;
      return {
        record,
        student: String(a.student || '').trim() || undefined,
        label: chipLabel(a.label),
      };
    },
    describe: (p) => p.label || 'See the question',
    execute: async () => 'Opened.',
  },
  // View-only (2026-08-14): walks the teacher to Intervene with that student's
  // detail open (their missed questions + the why panel). Uses Intervene's
  // existing ?student= deep link. AIAssistant intercepts; execute() unused.
  openStudent: {
    validate: (a) => {
      const student = String(a.student || '').trim();
      if (!student) return null;
      return { student, label: chipLabel(a.label) };
    },
    describe: (p) => p.label || 'Open in Intervene',
    execute: async () => 'Opened.',
  },
  // View-only handoff (2026-09-23): opens Create Assignments with the teacher's
  // request, and the assignment chat (also Buffy) drafts it there: skills,
  // count, levels, due date. Nothing is assigned until the teacher presses
  // Assign this homework / Start this unit. AIAssistant and HomeConversation
  // intercept this kind; execute() is never called.
  draftAssignment: {
    validate: (a) => {
      const type = a.type === 'unit' ? 'unit' : 'homework';
      const request = String(a.request || '').replace(/\s+/g, ' ').trim().slice(0, 600);
      if (type === 'homework' && !request) return null;
      // Exact local times from a schedule_assignment result (2026-10-20):
      // "YYYY-MM-DDTHH:mm" only; the Home draft card opens with them.
      const at = (v) => (type === 'homework' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v) ? v : null);
      const startAt = at(a.startAt);
      const dueDate = at(a.dueDate);
      return { type, request, label: chipLabel(a.label), ...(startAt ? { startAt } : {}), ...(dueDate ? { dueDate } : {}) };
    },
    describe: (p) => p.label || (p.type === 'unit' ? 'Set up the unit with me' : 'Draft it with me'),
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-06): opens the Students page's Reset password
  // dialog. The student is optional; without one the chat shows a student
  // picker first. The teacher types the temporary code in the dialog, so it
  // never passes through Buffy or the transcript.
  resetPassword: {
    validate: (a) => {
      const student = String(a.student || '').trim();
      const name = String(a.name || '').replace(/\s+/g, ' ').trim().slice(0, 80);
      return { student: student || undefined, name: student ? name || undefined : undefined };
    },
    describe: (p) => (p.student && p.name ? `Reset ${p.name}'s password` : "Reset a student's password"),
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-06): opens the Diagnostic page's Reset dialog for
  // one student. Student and test (1, 2 or 3, as teachers see them) are both
  // optional; the chat asks for whichever is missing.
  resetDiagnostic: {
    validate: diagnosticTarget,
    describe: (p) => `Reset ${whose(p)} ${p.test ? `Diagnostic ${p.test}` : 'diagnostic'}`,
    execute: async () => 'Opened.',
  },
  // Dialog actions (2026-10-06): the Diagnostic page's per-student timer
  // controls. The dialog loads live status and offers only what the page
  // would: Pause / Unpause, or Set time while the student is in the test.
  pauseDiagnostic: {
    validate: diagnosticTarget,
    describe: (p) => `Pause or unpause ${whose(p)} ${p.test ? `Diagnostic ${p.test}` : 'diagnostic'}`,
    execute: async () => 'Opened.',
  },
  setDiagnosticTime: {
    validate: diagnosticTarget,
    describe: (p) => `Set ${whose(p)} ${p.test ? `Diagnostic ${p.test} ` : ''}section time`,
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-06): Assignments' Finish homework confirm. The
  // homework (id or exact title) is optional; the dialog lists open ones.
  finishHomework: {
    validate: (a) => {
      const homework = String(a.homework || '').trim();
      const title = String(a.title || '').replace(/\s+/g, ' ').trim().slice(0, 120);
      return { homework: /^[\w-]{1,80}$/.test(homework) ? homework : undefined, title: title || undefined };
    },
    describe: (p) => (p.title ? `Finish “${chipLabel(p.title)}”` : 'Finish a homework'),
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-16): the assigned-set edit box opens in the chat
  // with the change the teacher asked for filled in (EditHomeworkChat); it
  // saves only on the teacher's Save changes. homework (id) or title names the
  // set; name, due, start ('now' opens it now), closeAtDue and students="open"
  // are the change. due/start "none" removes the due date. Anything else is
  // ignored: topics, difficulty and length are fixed once assigned.
  editHomework: {
    validate: (a) => {
      const homework = String(a.homework || '').trim();
      const title = clean(a.title, 120);
      const time = (v) => {
        const s = String(v || '').trim();
        if (!s) return undefined;
        if (/^(none|remove|clear)$/i.test(s)) return null;
        const full = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T23:59` : s;
        return Number.isFinite(Date.parse(full)) ? full : undefined;
      };
      const start = /^now$/i.test(String(a.start || '').trim()) ? null : time(a.start);
      const due = time(a.due);
      const closeAtDue = a.closeAtDue === 'true' ? true : a.closeAtDue === 'false' ? false : undefined;
      return {
        homework: /^[\w-]{1,80}$/.test(homework) ? homework : undefined, title: title || undefined,
        name: clean(a.name, 80) || undefined, due, start: start === undefined ? undefined : start,
        closeAtDue, students: a.students === 'open' || undefined,
      };
    },
    describe: (p) => (p.title ? `Edit “${chipLabel(p.title)}”` : 'Edit a practice set'),
    // A past due date is refused; a set named only by id gets its title (W4-B).
    prepare: async (auth, classId, p) => {
      if (p.due && isPast(p.due)) return { refused: PAST_DUE };
      if (!p.homework || p.title) return {};
      try {
        const hw = ((await teacherApi(auth, 'listHomework', { classID: classId }))?.items || []).find((h) => h.hwId === p.homework);
        return hw?.title ? { params: { ...p, title: clean(hw.title, 120) } } : {};
      } catch { return {}; }
    },
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-06): start or end the live session. The dialog
  // reads the current state first and offers only the matching control.
  liveSession: {
    validate: (a) => ({ intent: a.intent === 'start' || a.intent === 'end' ? a.intent : undefined }),
    describe: (p) => (p.intent === 'start' ? 'Start a live class session' : p.intent === 'end' ? 'End the live session' : 'Start or end the live session'),
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-13, BBO-82): Remove a student, the same call and
  // the same click-again confirm as Edit students. Student optional (picker).
  removeStudent: {
    validate: (a) => {
      const student = String(a.student || '').trim();
      const name = clean(a.name, 80);
      return { student: student || undefined, name: student ? name || undefined : undefined };
    },
    describe: (p) => (p.student && p.name ? `Remove ${p.name} from this class` : 'Remove a student from this class'),
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-13): the class switcher's Manage class dialog,
  // opened at the part the teacher asked about. `query` prefills the Edit
  // students search (a student to add or find). Deleting or leaving still takes
  // the dialog's own click-again confirm.
  // For 'details', name and period prefill the fields (a rename the teacher
  // asked for); the teacher still presses Save.
  manageClass: {
    validate: (a) => {
      const section = Object.keys(CLASS_SECTIONS).includes(a.section) ? a.section : undefined;
      const query = section === 'students' ? clean(a.query, 60) : '';
      const name = section === 'details' ? clean(a.name, 80) : '';
      const period = section === 'details' && a.period != null ? clean(a.period, 20) : undefined;
      return { section, query: query || undefined, name: name || undefined, period };
    },
    describe: (p) => (p.section === 'students' && p.query ? `Find ${chipLabel(p.query)} in Edit students`
      : p.section === 'details' && p.name ? `Rename this class to “${chipLabel([p.period, p.name].filter(Boolean).join(' · '))}”`
        : CLASS_SECTIONS[p.section] || 'Manage this class'),
    execute: async () => 'Opened.',
  },
  // Dialog action (2026-10-13): the New class dialog, with the name and period
  // the teacher gave prefilled. It shows the new join code when it's made.
  newClass: {
    validate: (a) => ({ name: clean(a.name, 60) || undefined, period: clean(a.period, 20) || undefined }),
    describe: (p) => (p.name ? `Make a new class: ${chipLabel([p.period, p.name].filter(Boolean).join(' · '))}` : 'Make a new class'),
    execute: async () => 'Opened.',
  },
  // The one memory-write chip: saves the teacher's own words to Sandy's notes
  // (ClaudeSandyMemory via teacher-api). Auto-memory covers activity signals;
  // free-text facts only land here, teacher-approved verbatim.
  remember: {
    validate: (a) => {
      const text = String(a.text || '').trim().slice(0, 200);
      return text ? { text } : null;
    },
    describe: (p) => `Save to Buffy's notes: “${p.text}”`,
    execute: async (auth, classId, p) => {
      await teacherApi(auth, 'putSandyMemoryNote', { classID: classId, text: p.text });
      return 'Noted. I’ll keep that in mind for this class.';
    },
  },
};

export function resolveSandyAction(attrs) {
  if (!sandyActionsEnabled()) return null;
  const def = SANDY_ACTIONS[attrs.kind];
  if (!def) return null;
  const params = def.validate(attrs);
  if (!params) return null;
  return { kind: attrs.kind, params, text: def.describe(params) };
}

// What a chip needs from the class before it shows (W4-B): a kind's
// prepare() may refuse it (a plain line in its place) or fill in params its
// label names. -> the action, with `refused` (text) when it must not run.
export async function prepareSandyAction(auth, classId, action) {
  const def = SANDY_ACTIONS[action?.kind];
  if (!def?.prepare) return action;
  const { refused, params } = (await def.prepare(auth, classId, action.params)) || {};
  if (refused) return { ...action, refused };
  return params ? { ...action, params, text: def.describe(params) } : action;
}

// ---- the target check (W4-B, review finding 3) ----
// A student id and a set id come from the model. Before a chip that acts on
// one runs or opens its dialog, the id must be in this class's roster (or
// homework list), and a name given with a student must be that student's.
// Otherwise the chip says so in a plain line and nothing is called.
export const STUDENT_ACTION_KINDS = ['release', 'remind', 'markAbsent', 'resetDiagnostic', 'resetPassword', 'pauseDiagnostic', 'setDiagnosticTime', 'removeStudent', 'showQuestion', 'openStudent'];
export const HOMEWORK_ACTION_KINDS = ['finishHomework', 'editHomework'];
export const NO_STUDENT_MATCH = 'I can’t match that student to this class.';
export const NO_SET_MATCH = 'I can’t match that practice set to this class.';
const NO_CHECK = 'I can’t check this class’s list right now, so I didn’t do that. Try again in a moment.';

const normName = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
// "Ann" or "Ann Lee" names Ann Lee; "Ann L" or "Ben" does not.
export const namesRosterStudent = (said, name) => {
  const s = normName(said);
  const n = normName(name);
  return !s || s === n || n.split(' ')[0] === s;
};
// -> the roster row the id and name agree on, or null.
export function rosterStudent(roster, id, name) {
  const row = (roster || []).find((r) => r.userId === id);
  return row && namesRosterStudent(name, row.name) ? row : null;
}

// -> { ok: true, student? } or { ok: false, text }. `student` is the roster
// row, so the chip and its done note use the class's own spelling.
export async function checkActionTarget(auth, classId, action) {
  const p = action?.params || {};
  if (p.student && STUDENT_ACTION_KINDS.includes(action.kind)) {
    let roster;
    try { roster = await fetchRoster(auth, classId); } catch { return { ok: false, text: NO_CHECK, retry: true }; }
    const student = rosterStudent(roster, p.student, p.name);
    return student ? { ok: true, student } : { ok: false, text: NO_STUDENT_MATCH };
  }
  if (p.homework && HOMEWORK_ACTION_KINDS.includes(action.kind)) {
    let items;
    try { items = (await teacherApi(auth, 'listHomework', { classID: classId }))?.items || []; } catch { return { ok: false, text: NO_CHECK, retry: true }; }
    return items.some((hw) => hw.hwId === p.homework) ? { ok: true } : { ok: false, text: NO_SET_MATCH };
  }
  return { ok: true };
}

// A refused check throws with `refused` set: the chip shows the line as it
// is, with no retry (a list that failed to load may be tried again).
export async function executeSandyAction(auth, classId, action) {
  const check = await checkActionTarget(auth, classId, action);
  if (!check.ok) throw Object.assign(new Error(check.text), { refused: !check.retry });
  const params = check.student ? { ...action.params, name: check.student.name } : action.params;
  return SANDY_ACTIONS[action.kind].execute(auth, classId, params);
}
